import styled, { css, keyframes } from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import theme from "theme/themeThree";

export const Container = styled(Box)<{ $fullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ $fullscreen }) =>
    $fullscreen ? "100vh" : "calc(100vh - 73px)"};
  max-height: ${({ $fullscreen }) =>
    $fullscreen ? "100vh" : "calc(100vh - 73px)"};
  background: #0b1116;
  color: #f7fcfd;
  overflow: hidden;

  ${({ $fullscreen }) =>
    $fullscreen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
  `}
`;

export const HeaderWrapper = styled(Box)<{ $bgImage?: string }>`
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${({ $bgImage }) =>
      $bgImage ? `url(${$bgImage})` : "none"};
    background-size: cover;
    background-position: center 20%;
    filter: blur(5px);
    opacity: 0.3;
    transform: scale(1);
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(19, 29, 37, 0.75) 0%,
      rgba(19, 29, 37, 0.85) 100%
    );
    z-index: 1;
  }
`;

export const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  position: relative;
  z-index: 2;
  border-bottom: 1px solid rgba(30, 46, 58, 0.5);
`;

export const MapBadge = styled(Box)`
  padding: 6px 12px;
  background: rgba(113, 170, 255, 0.1);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 6px;
`;

export const MainContent = styled(Flex)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

export const TimelinePanel = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0b1116;
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

export const ControlBar = styled(Flex)<{ $inWrapper?: boolean }>`
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${({ $inWrapper }) => ($inWrapper ? "transparent" : "#131d25")};
  border-bottom: 1px solid #1e2e3a;
  position: relative;
  z-index: 2;

  @media (max-width: 1600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const RoundIndicator = styled(Flex)`
  align-items: center;
  gap: 3px;
  padding: 0 12px;
  border-left: 1px solid #253a49;
  margin-left: 8px;
`;

export const RoundPip = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 32px;
  padding: 2px 4px;
  border: 2px solid ${({ active }) => (active ? "#71aaff" : "transparent")};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.25)" : "rgba(143, 169, 175, 0.15)"};

  .round-num {
    font-size: 10px;
    font-weight: 600;
    color: ${({ active }) => (active ? "#71aaff" : "#8fa9af")};
    line-height: 1;
  }

  .kills-count {
    font-size: 8px;
    color: #557077;
    line-height: 1;
  }

  &:hover {
    background: rgba(113, 170, 255, 0.35);
    transform: translateY(-2px);
  }
`;

export const HalftimeDivider = styled.div`
  width: 2px;
  height: 24px;
  background: linear-gradient(to bottom, transparent, #557077, transparent);
  margin: 0 4px;
`;

export const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #253a49;
    color: #f7fcfd;
  }
`;

export const WeaponSelect = styled.select`
  padding: 6px 28px 6px 10px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238fa9af'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:hover {
    background-color: #253a49;
    border-color: #71aaff;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
  }

  option {
    background: #1a2933;
    color: #f7fcfd;
  }
`;

export const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

export const ToggleCheckbox = styled.input`
  display: none;
`;

export const ToggleSlider = styled.span`
  position: relative;
  width: 36px;
  height: 20px;
  background: #253a49;
  border-radius: 10px;
  transition: all 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #8fa9af;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  ${ToggleCheckbox}:checked + & {
    background: #4dbd94;
  }

  ${ToggleCheckbox}:checked + &::after {
    left: 18px;
    background: #0b1116;
  }
`;

export const TimelineContainerWrapper = styled(Box)`
  flex: 1;
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

export const TimelineContainer = styled(Box)`
  width: 100%;
  height: calc(100% + 20px); /* Extra height to push scrollbar out of view */
  overflow-x: scroll;
  overflow-y: auto;
  position: relative;
  background: #0b1116;
  padding-bottom: 20px; /* Compensate for the extra height */

  /* Vertical scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 20px;
  }

  &::-webkit-scrollbar-track:vertical {
    background: #0b1116;
  }

  &::-webkit-scrollbar-thumb:vertical {
    background: #253a49;
    border-radius: 4px;

    &:hover {
      background: #3d5a6e;
    }
  }

  &::-webkit-scrollbar-corner {
    background: #0b1116;
  }
`;

export const CustomScrollbarContainer = styled(Box)`
  position: relative;
  height: 24px;
  background: #131d25;
  border-top: 1px solid #1e2e3a;
  padding: 4px 16px 4px 216px;
  flex-shrink: 0;
`;

export const CustomScrollbarTrack = styled(Box)`
  position: relative;
  height: 16px;
  background: #1a2530;
  border-radius: 8px;
  border: 1px solid #253a49;
  cursor: pointer;
`;

export const CustomScrollbarThumb = styled(Box)<{ $isDragging?: boolean }>`
  position: absolute;
  top: 2px;
  height: 10px;
  min-width: 40px;
  background: ${({ $isDragging }) =>
    $isDragging
      ? "linear-gradient(180deg, #71aaff 0%, #5090e0 100%)"
      : "linear-gradient(180deg, #4d6a7e 0%, #3d5665 100%)"};
  border-radius: 5px;
  cursor: grab;
  transition: ${({ $isDragging }) =>
    $isDragging ? "none" : "background 0.15s ease"};

  &:hover {
    background: linear-gradient(180deg, #5d7a8e 0%, #4d6675 100%);
  }

  &:active {
    cursor: grabbing;
    background: linear-gradient(180deg, #71aaff 0%, #5090e0 100%);
  }
`;

export const TimelineInner = styled(Box)`
  position: relative;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

export const TimelineRuler = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #131d25;
`;

export const RulerHeader = styled(Flex)`
  height: 24px;
`;

export const RoundsHeader = styled(Flex)`
  height: 28px;
  background: #0f171d;
`;

export const PlayerColumnHeader = styled(Flex)`
  position: sticky;
  left: 0;
  z-index: 15;
  align-items: center;
  width: 200px;
  min-width: 200px;
  padding: 0 12px;
  background: #131d25;
  font-size: 11px;
  font-weight: 600;
  color: #557077;
`;

export const RulerRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
  border-bottom: 1px solid #1e2e3a;
`;

export const RulerMark = styled(Box)`
  position: absolute;
  top: 0;
  height: 100%;
  border-left: 1px solid #253a49;

  span {
    position: absolute;
    top: 4px;
    left: 4px;
    font-size: 10px;
    color: #557077;
    white-space: nowrap;
  }
`;

export const RoundsRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
`;

export const RoundBlock = styled(Box)`
  position: absolute;
  height: 100%;
  background: rgba(143, 169, 175, 0.06);
  border-left: 1px solid rgba(143, 169, 175, 0.2);
  border-bottom: 2px solid rgba(143, 169, 175, 0.15);
`;

export const RoundLabel = styled(Flex)`
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(247, 252, 253, 0.7);
`;

export const RoundScore = styled.span`
  font-size: 10px;
  font-weight: 600;

  .ct {
    color: #4da9c9;
  }

  .separator {
    color: rgba(143, 169, 175, 0.5);
    margin: 0 1px;
  }

  .t {
    color: #dea34e;
  }
`;

export const PlayersContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const TeamDivider = styled(Flex)`
  align-items: center;
  height: 24px;
  margin: 8px 0;
  padding: 0 12px;
`;

export const TeamDividerLine = styled.div`
  flex: 1;
  height: 2px;
  background: linear-gradient(to right, #8fa9af, rgba(143, 169, 175, 0.1));
  border-radius: 1px;
`;

export const PlayerRow = styled(Flex)<{ selected: boolean; team?: "ct" | "t" }>`
  position: relative;
  height: 56px;
  margin: 4px 0;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 4px;

  &:hover {
    background: ${({ team }) =>
      team === "ct" ? "rgba(92, 155, 255, 0.08)" : "rgba(232, 169, 62, 0.08)"};
  }

  ${({ selected, team }) =>
    selected &&
    css`
      background: ${team === "ct"
        ? "rgba(92, 155, 255, 0.2)"
        : "rgba(232, 169, 62, 0.2)"};
      box-shadow: inset 0 0 0 2px
        ${team === "ct" ? "rgba(92, 155, 255, 0.4)" : "rgba(232, 169, 62, 0.4)"};
    `}
`;

export const PlayerInfo = styled(Flex)<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  position: sticky;
  left: 0;
  z-index: 100;
  align-items: center;
  gap: 10px;
  width: 200px;
  min-width: 200px;
  padding: 8px 12px;
  background: ${({ selected, isCurrentUser, team }) =>
    isCurrentUser
      ? selected
        ? "#1a3d2e"
        : "#152a20"
      : selected
      ? team === "ct"
        ? "#1a3348"
        : "#2d2a1a"
      : team === "ct"
      ? "#131d25"
      : "#1a1815"};
  border-right: 1px solid #1e2e3a;
  border-left: 4px solid
    ${({ selected, isCurrentUser, team }) =>
      isCurrentUser
        ? "#4dbd94"
        : selected
        ? team === "ct"
          ? "#5C9BFF"
          : "#E8A93E"
        : team === "ct"
        ? "rgba(92, 155, 255, 0.3)"
        : "rgba(232, 169, 62, 0.3)"};
`;

export const PlayerAvatar = styled(Flex)<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ selected, isCurrentUser, team }) =>
    isCurrentUser
      ? "#4dbd94"
      : selected
      ? team === "ct"
        ? "#5C9BFF"
        : "#E8A93E"
      : "#253a49"};
  color: ${({ selected, isCurrentUser }) =>
    isCurrentUser || selected ? "#0b1116" : "#8fa9af"};
  box-shadow: ${({ selected, isCurrentUser, team }) =>
    selected && !isCurrentUser
      ? team === "ct"
        ? "0 0 0 2px rgba(92, 155, 255, 0.5)"
        : "0 0 0 2px rgba(232, 169, 62, 0.5)"
      : isCurrentUser
      ? "0 0 0 2px rgba(77, 189, 148, 0.5)"
      : "none"};
`;

export const PlayerAvatarImage = styled.img<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  border-radius: 50%;
  box-shadow: ${({ selected, isCurrentUser, team }) =>
    selected && !isCurrentUser
      ? team === "ct"
        ? "0 0 0 2px rgba(92, 155, 255, 0.5)"
        : "0 0 0 2px rgba(232, 169, 62, 0.5)"
      : isCurrentUser
      ? "0 0 0 2px rgba(77, 189, 148, 0.5)"
      : "none"};
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

export const PlayerDetails = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const PlayerName = styled(Text)<{
  selected: boolean;
  isCurrentUser?: boolean;
}>`
  font-size: 13px;
  font-weight: ${({ selected, isCurrentUser }) =>
    selected || isCurrentUser ? 600 : 500};
  color: ${({ selected, isCurrentUser }) =>
    isCurrentUser ? "#5cd4a6" : selected ? "#5C9BFF" : "#8fa9af"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const YouBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #0b1116;
  background: #4dbd94;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
`;

export const PlayerStats = styled(Flex)`
  gap: 8px;
  margin-top: 2px;
`;

export const StatBadge = styled(Flex)<{ type: "kills" | "deaths" }>`
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: ${({ type }) => (type === "kills" ? "#4dbd94" : "#f95370")};
`;

export const PlayerTimeline = styled(Box)<{ selected: boolean }>`
  position: relative;
  flex: 1;
  height: 100%;
  cursor: ${({ selected }) => (selected ? "crosshair" : "pointer")};
`;

export const RoundSegment = styled(Box)<{
  team: "ct" | "t" | null;
  selected: boolean;
}>`
  position: absolute;
  top: 8px;
  height: calc(100% - 16px);
  background: ${({ team, selected }) =>
    team === "ct"
      ? selected
        ? "rgba(92, 155, 255, 0.15)"
        : "rgba(92, 155, 255, 0.08)"
      : team === "t"
      ? selected
        ? "rgba(232, 169, 62, 0.15)"
        : "rgba(232, 169, 62, 0.08)"
      : "rgba(143, 169, 175, 0.04)"};
  border-left: 1px solid rgba(143, 169, 175, 0.15);
  border-bottom: 2px solid
    ${({ team, selected }) =>
      team === "ct"
        ? selected
          ? "rgba(92, 155, 255, 0.5)"
          : "rgba(92, 155, 255, 0.25)"
        : team === "t"
        ? selected
          ? "rgba(232, 169, 62, 0.5)"
          : "rgba(232, 169, 62, 0.25)"
        : "rgba(143, 169, 175, 0.1)"};
`;

export const KillMarker = styled(Flex)<{
  isHeadshot: boolean;
  selected: boolean;
  inSegment?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  align-items: center;
  gap: 4px;
  padding: ${({ selected }) => (selected ? "4px 8px" : "4px 6px")};
  background: ${({ isHeadshot }) => (isHeadshot ? "#e8a93e" : "#4dbd94")};
  border-radius: ${({ selected }) => (selected ? "12px" : "10px")};
  color: #0b1116;
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;
  min-width: 24px;
  min-height: 20px;
  justify-content: center;
  box-shadow: ${({ inSegment }) =>
    inSegment ? "0 0 0 2px #0b1116, 0 0 0 4px #f7fcfd" : "none"};

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
  }
`;

export const WeaponIcon = styled.img`
  height: 14px;
  width: auto;
  max-width: 28px;
  filter: brightness(0) saturate(100%);
  object-fit: contain;
`;

export const KillLabel = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #0b1116;
  white-space: nowrap;
`;

export const DeathMarker = styled(Flex)<{
  selected: boolean;
  inSegment?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(249, 83, 112, 0.3);
  border: 1px solid #f95370;
  border-radius: 50%;
  color: #f95370;
  opacity: ${({ selected }) => (selected ? 1 : 0.5)};
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;
  box-shadow: ${({ inSegment }) =>
    inSegment ? "0 0 0 2px #0b1116, 0 0 0 4px #f7fcfd" : "none"};

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
    opacity: 1;
    background: rgba(249, 83, 112, 0.5);
  }
`;

export const SelectionOverlay = styled(Box)<{
  isActive?: boolean;
  wouldMerge?: boolean;
  selected?: boolean;
}>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ isActive, wouldMerge, selected }) =>
    wouldMerge
      ? "rgba(113, 170, 255, 0.4)"
      : selected
      ? "rgba(77, 189, 148, 0.4)"
      : isActive
      ? "rgba(77, 189, 148, 0.3)"
      : "rgba(77, 189, 148, 0.2)"};
  border: 2px solid
    ${({ isActive, wouldMerge, selected }) =>
      wouldMerge
        ? "#71AAFF"
        : selected
        ? "#f7fcfd"
        : isActive
        ? "#5cd4a6"
        : "#4dbd94"};
  border-radius: 6px;
  z-index: 4;
  transition: all 0.15s ease;
  cursor: pointer;
`;

export const PreviewOverlay = styled(Box)<{ wouldMerge?: boolean }>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ wouldMerge }) =>
    wouldMerge ? "rgba(113, 170, 255, 0.2)" : "rgba(77, 189, 148, 0.15)"};
  border: 2px dashed ${({ wouldMerge }) => (wouldMerge ? "#71AAFF" : "#4dbd94")};
  border-radius: 6px;
  z-index: 3;
  pointer-events: none;
`;

export const SelectionHandle = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 28px;
  background: #4dbd94;
  border-radius: 3px;
  cursor: ew-resize;
  z-index: 10;
  transition: background 0.15s ease;

  &:hover {
    background: #5dd4a8;
  }

  &.left {
    left: -4px;
  }
  &.right {
    right: -4px;
  }
`;

export const SelectionsPanel = styled(Box)<{ collapsed?: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "48px" : "400px")};
  background: #131d25;
  border-left: 1px solid #1e2e3a;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  overflow: hidden;
`;

export const PanelHeader = styled(Box)`
  padding: 16px;
  border-bottom: 1px solid #1e2e3a;
`;

export const PanelHeaderTop = styled(Flex)`
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(143, 169, 175, 0.1);
  border: none;
  border-radius: 4px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(143, 169, 175, 0.2);
    color: #f7fcfd;
  }
`;

export const Toast = styled(Box)`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #1a2933 0%, #253a49 100%);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  color: #f7fcfd;
  font-size: 13px;
  font-weight: 500;
  z-index: 1000;
  animation: slideUp 0.3s ease;

  svg {
    color: #71aaff;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

export const SelectionsList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #0b1116;
  }
  &::-webkit-scrollbar-thumb {
    background: #253a49;
    border-radius: 3px;
  }
`;

export const EmptyState = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
`;

export const EmptyIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #1a2933;
  border-radius: 50%;
  color: #557077;
`;

const highlightPulse = keyframes`
  0% {
    border-color: #71aaff;
    box-shadow: 0 0 0 0 rgba(113, 170, 255, 0.4);
  }
  50% {
    border-color: #71aaff;
    box-shadow: 0 0 8px 2px rgba(113, 170, 255, 0.3);
  }
  100% {
    border-color: #1e2e3a;
    box-shadow: 0 0 0 0 rgba(113, 170, 255, 0);
  }
`;

export const SelectionCard = styled(Box)<{
  selected?: boolean;
  recentlyAdded?: boolean;
}>`
  padding: 10px 12px;
  margin-bottom: 8px;
  background: ${({ selected }) => (selected ? "#1a2a36" : "#16232c")};
  border: 1px solid ${({ selected }) => (selected ? "#4dbd94" : "#1e2e3a")};
  border-radius: 8px;
  transition: all 0.15s ease;
  overflow: hidden;
  cursor: pointer;

  ${({ recentlyAdded }) =>
    recentlyAdded &&
    css`
      animation: ${highlightPulse} 2s ease-out forwards;
    `}

  &:hover {
    border-color: ${({ selected }) => (selected ? "#4dbd94" : "#253a49")};
  }
`;

export const ClipHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

export const DurationSlider = styled(Box)`
  margin: 8px 0;
  padding: 0 4px;
`;

export const DurationTrack = styled(Box)`
  position: relative;
  height: 8px;
  background: rgba(37, 58, 73, 0.8);
  border-radius: 4px;
  margin-bottom: 4px;
`;

export const DurationFill = styled(Box)`
  position: absolute;
  top: 0;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(77, 189, 148, 0.4),
    rgba(77, 189, 148, 0.6)
  );
  border-radius: 4px;
`;

export const DurationKillMarker = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: #4dbd94;
  border-radius: 50%;
  z-index: 2;
`;

export const DurationDeathMarker = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: #e74c3c;
  border-radius: 50%;
  z-index: 2;
`;

export const DurationLabels = styled(Flex)`
  justify-content: space-between;
  font-size: 12px;
  color: #557077;
`;

export const DurationTimingLabels = styled(Flex)`
  justify-content: center;
  gap: 16px;
  font-size: 12px;
  color: #8fa3ad;
  margin-top: 4px;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .label {
    color: #557077;
  }

  .value {
    color: #4dbd94;
    font-weight: 500;
  }
`;

export const KillsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const KillItem = styled(Box)`
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

export const KillItemHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 12px;

  .killer {
    color: #f7fcfd;
    font-weight: 500;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .victim {
    color: #8fa9af;
    font-weight: 500;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hs {
    font-size: 9px;
    padding: 1px 4px;
    background: rgba(249, 83, 112, 0.3);
    color: #f95370;
    border-radius: 3px;
    font-weight: 600;
  }
`;

export const KillWeaponIcon = styled.img`
  width: 24px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(73%) sepia(9%) saturate(590%)
    hue-rotate(144deg) brightness(90%) contrast(86%);
`;

export const KillItemInfo = styled(Box)`
  flex: 1;

  .weapon {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #f7fcfd;

    .hs {
      font-size: 10px;
      padding: 2px 5px;
      background: rgba(249, 83, 112, 0.3);
      color: #f95370;
      border-radius: 3px;
    }
  }

  .players {
    font-size: 12px;
    color: #557077;
  }
`;

export const PovSection = styled(Box)`
  margin-bottom: 10px;
`;

export const PovLabel = styled(Text)`
  font-size: 11px;
  color: #557077;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PovToggle = styled(Flex)`
  gap: 8px;
`;

const getTeamColors = (
  team: "ct" | "t" | null | undefined,
  active: boolean,
) => {
  if (team === "ct") {
    return {
      bg: active ? "rgba(92, 155, 255, 0.35)" : "rgba(92, 155, 255, 0.08)",
      border: active ? "#5c9bff" : "rgba(92, 155, 255, 0.3)",
      color: active ? "#fff" : "#7eb3ff",
      shadow: active ? "0 0 8px rgba(92, 155, 255, 0.4)" : "none",
      hoverBg: "rgba(92, 155, 255, 0.15)",
    };
  } else if (team === "t") {
    return {
      bg: active ? "rgba(232, 169, 62, 0.35)" : "rgba(232, 169, 62, 0.08)",
      border: active ? "#e8a93e" : "rgba(232, 169, 62, 0.3)",
      color: active ? "#fff" : "#f0c06a",
      shadow: active ? "0 0 8px rgba(232, 169, 62, 0.4)" : "none",
      hoverBg: "rgba(232, 169, 62, 0.15)",
    };
  }
  return {
    bg: active ? "rgba(113, 170, 255, 0.3)" : "transparent",
    border: active ? "#71aaff" : "#253a49",
    color: active ? "#fff" : "#557077",
    shadow: active ? "0 0 8px rgba(113, 170, 255, 0.4)" : "none",
    hoverBg: "rgba(113, 170, 255, 0.1)",
  };
};

export const PovOption = styled.button<{
  active: boolean;
  team?: "ct" | "t" | null;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: ${({ active, team }) => getTeamColors(team, active).bg};
  border: 2px solid ${({ active, team }) => getTeamColors(team, active).border};
  border-radius: 6px;
  font-size: 11px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ active, team }) => getTeamColors(team, active).color};
  box-shadow: ${({ active, team }) => getTeamColors(team, active).shadow};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  flex: 1;
  justify-content: center;
  position: relative;

  .player-name {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70px;
  }

  .check-icon {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 14px;
    height: 14px;
    background: ${({ team }) =>
      team === "ct" ? "#5c9bff" : team === "t" ? "#e8a93e" : "#71aaff"};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0b1116;
    font-size: 8px;
  }

  &:hover {
    background: ${({ team }) => getTeamColors(team, true).hoverBg};
    border-color: ${({ team }) => getTeamColors(team, true).border};
  }
`;

export const PovAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const PovAvatarPlaceholder = styled(Flex)<{ team?: "ct" | "t" | null }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ team }) =>
    team === "ct"
      ? "rgba(92, 155, 255, 0.3)"
      : team === "t"
      ? "rgba(232, 169, 62, 0.3)"
      : "rgba(143, 169, 175, 0.3)"};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ team }) =>
    team === "ct" ? "#5c9bff" : team === "t" ? "#e8a93e" : "#8fa9af"};
`;

export const MountedCameraSection = styled(Box)`
  margin-top: 8px;
`;

export const MountedCameraHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 8px;

  svg {
    color: #71aaff;
  }
`;

export const MountedCameraOptions = styled(Flex)`
  flex-wrap: wrap;
  gap: 6px;
`;

export const MountedCameraOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.2)" : "rgba(30, 46, 58, 0.5)"};
  border: 1px solid ${({ active }) => (active ? "#71aaff" : "#253a49")};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  .label {
    font-size: 11px;
    font-weight: ${({ active }) => (active ? 600 : 400)};
    color: ${({ active }) => (active ? "#71aaff" : "#8fa9af")};
  }

  &:hover {
    background: rgba(113, 170, 255, 0.15);
    border-color: #71aaff;

    .label {
      color: #71aaff;
    }
  }
`;

export const SectionDivider = styled(Box)`
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(37, 58, 73, 0.8),
    transparent
  );
  margin: 12px 0;
`;

export const CameraOptionsSection = styled(Box)`
  padding: 10px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  margin-top: 4px;
`;

export const CameraOptionsHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 10px;

  svg {
    color: #71aaff;
  }
`;

export const CameraKillItem = styled(Box)`
  &:not(:last-child) {
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(37, 58, 73, 0.5);
  }
`;

export const CameraKillHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 10px;

  .killer {
    color: #f7fcfd;
    font-weight: 500;
  }

  .victim {
    color: #8fa9af;
    font-weight: 500;
  }
`;

export const TimeAdjustRow = styled(Flex)`
  gap: 10px;
  width: 100%;
`;

export const TimeAdjustGroup = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const TimeLabel = styled(Text)`
  font-size: 11px;
  color: #8fa9af;
  margin-bottom: 6px;
  display: block;
`;

export const TimeInputWrapper = styled(Flex)`
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
`;

export const TimeIncrementBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 3px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #253a49;
    color: #f7fcfd;
  }

  &:active:not(:disabled) {
    background: #71aaff;
    color: #0b1116;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const TimeInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  background: #0b1116;
  border: 1px solid #253a49;
  border-radius: 4px;
  color: #f7fcfd;
  font-size: 13px;
  font-family: monospace;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #71aaff;
  }
`;

export const ClipNumber = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: #8fa9af;
  margin-right: 8px;
`;

export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #557077;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #f95370;
    background: rgba(249, 83, 112, 0.15);
  }
`;

export const PanelFooter = styled(Box)`
  padding: 16px;
  border-top: 1px solid #1e2e3a;
  background: #0f171d;
`;

export const ClipTitleSection = styled(Box)`
  margin-bottom: 12px;
`;

export const ClipTitleLabel = styled(Text)`
  font-size: 13px;
  color: #8fa9af;
  margin-bottom: 8px;
  display: block;
`;

export const ClipTitleInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #0b1116;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 13px;
  box-sizing: border-box;
  transition: all 0.15s ease;

  &::placeholder {
    color: #557077;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
    background: #131d25;
  }

  &:hover:not(:focus) {
    border-color: #3a5066;
  }
`;

export const TotalDuration = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 14px;
  background: linear-gradient(135deg, #4dbd94 0%, #3a9d7a 100%);
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5cd4a6 0%, #4dbd94 100%);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Tooltip = styled(Box)`
  position: fixed;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 8px;
  padding: 12px;
  z-index: 100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  max-width: 220px;
`;

export const TooltipTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #253a49;
`;

export const TooltipRow = styled(Box)`
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 4px;

  span {
    color: #557077;
    margin-right: 4px;
  }
`;

export const TooltipAction = styled(Text)`
  font-size: 11px;
  color: #4dbd94;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #253a49;
`;

export const TooltipMerge = styled(Text)`
  font-size: 11px;
  color: #71aaff;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #253a49;
`;

export const LoadingContainer = styled(Flex)`
  height: calc(100vh - 73px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #0b1116;
  gap: 24px;
`;

export const LoadingSpinner = styled(Box)`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(77, 189, 148, 0.15);
  border-top-color: #4dbd94;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingContent = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const LoadingTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: #f7fcfd;
`;

export const LoadingSubtitle = styled(Text)`
  font-size: 13px;
  color: #557077;
`;

export const LoadingProgress = styled(Box)`
  width: 200px;
  height: 4px;
  background: rgba(143, 169, 175, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
`;

export const LoadingProgressBar = styled(Box)`
  height: 100%;
  width: 30%;
  background: linear-gradient(90deg, #4dbd94, #71aaff);
  border-radius: 2px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      width: 0%;
      margin-left: 0%;
    }
    50% {
      width: 40%;
      margin-left: 30%;
    }
    100% {
      width: 0%;
      margin-left: 100%;
    }
  }
`;

export const WelcomeModalOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

export const WelcomeModalContent = styled(Box)`
  background: linear-gradient(135deg, #131d25 0%, #1a2933 100%);
  border: 1px solid #253a49;
  border-radius: 16px;
  padding: 32px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #4dbd94, #71aaff);
  }
`;

export const WelcomeModalTitle = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
`;

export const WelcomeModalSubtitle = styled(Text)`
  font-size: 14px;
  color: #71aaff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const WelcomeModalSection = styled(Box)`
  margin-bottom: 20px;
`;

export const WelcomeModalSectionTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #8fa9af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

export const WelcomeModalList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const WelcomeModalListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #8fa9af;
  line-height: 1.5;
`;

export const WelcomeModalListNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  background: rgba(77, 189, 148, 0.15);
  border: 1px solid rgba(77, 189, 148, 0.3);
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  color: #4dbd94;
  flex-shrink: 0;
`;

export const WelcomeModalShortcuts = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(143, 169, 175, 0.1);
`;

export const WelcomeModalShortcutItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8fa9af;
`;

export const WelcomeModalWarning = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(232, 169, 62, 0.1);
  border: 1px solid rgba(232, 169, 62, 0.25);
  border-radius: 8px;
  margin-bottom: 20px;

  svg {
    color: #e8a93e;
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 12px;
    color: #8fa9af;
    line-height: 1.5;
  }
`;

export const WelcomeModalDiscord = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(88, 101, 242, 0.15);
  border: 1px solid rgba(88, 101, 242, 0.3);
  border-radius: 8px;
  color: #8fa9af;
  font-size: 13px;
  text-decoration: none;
  transition: all 0.15s ease;
  margin-bottom: 20px;

  svg {
    color: #5865f2;
  }

  &:hover {
    background: rgba(88, 101, 242, 0.25);
    border-color: rgba(88, 101, 242, 0.5);
    color: #f7fcfd;
  }
`;

export const WelcomeModalButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: linear-gradient(135deg, #4dbd94, #3a9d7a);
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, #5cd4a6, #4dbd94);
    transform: translateY(-1px);
  }
`;

export const WelcomeModalKeyboard = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  background: rgba(143, 169, 175, 0.15);
  border: 1px solid rgba(143, 169, 175, 0.3);
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
  color: #8fa9af;
`;

export const UpsellBadgeContainer = styled.div`
  margin-bottom: 16px;
`;

export const SeeAllPlansLink = styled(Text)`
  color: rgba(143, 169, 175, 0.8);
  cursor: pointer;
  font-size: 12px;
  margin-top: 12px;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: #f7fcfd;
  }
`;
